import { Delete, EditOutlined } from '@mui/icons-material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import Header from '../../components/Header';
import Alert from '../../components/common/Alert';
import {
  createCoupon,
  deleteCoupon,
  getAllCoupons,
  updateCoupon,
} from '../../api/axios';

const initialForm = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: '',
  minOrderAmount: '',
  maxUses: '',
  expiresAt: '',
  isActive: true,
};

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [editingCouponId, setEditingCouponId] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const loadCoupons = async () => {
    setLoading(true);
    try {
      const response = await getAllCoupons();
      setCoupons(response.result || []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
      setSnackbarMessage(error.response?.data?.message || 'Failed to load coupons.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoupons();
  }, []);

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingCouponId(null);
    setFormData(initialForm);
  };

  const handleEdit = (coupon) => {
    setEditingCouponId(coupon.id);
    setFormData({
      code: coupon.code || '',
      description: coupon.description || '',
      discountType: coupon.discountType || 'percentage',
      discountValue:
        coupon.discountValue !== undefined && coupon.discountValue !== null
          ? coupon.discountValue
          : coupon.discountPercentage || '',
      minOrderAmount: coupon.minOrderAmount || '',
      maxUses: coupon.maxUses || '',
      expiresAt: coupon.expiresAt
        ? new Date(coupon.expiresAt).toISOString().slice(0, 16)
        : '',
      isActive: Boolean(coupon.isActive),
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        code: formData.code.trim().toUpperCase(),
      };

      if (editingCouponId) {
        await updateCoupon(editingCouponId, payload);
        setSnackbarMessage('Coupon updated successfully.');
      } else {
        await createCoupon(payload);
        setSnackbarMessage('Coupon created successfully.');
      }

      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      handleDialogClose();
      await loadCoupons();
    } catch (error) {
      console.error('Failed to save coupon:', error);
      setSnackbarMessage(error.response?.data?.message || 'Failed to save coupon');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Delete this coupon?');
    if (!confirmed) {
      return;
    }

    try {
      await deleteCoupon(id);
      await loadCoupons();
      setSnackbarMessage('Coupon deleted successfully.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Failed to delete coupon:', error);
      setSnackbarMessage(error.response?.data?.message || 'Failed to delete coupon');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.35 },
    { field: 'code', headerName: 'Code', flex: 0.8 },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.4,
      valueGetter: (params) => params.row.description || 'No description',
    },
    {
      field: 'discountValue',
      headerName: 'Discount',
      flex: 0.9,
      renderCell: ({ row }) =>
        row.discountType === 'fixed'
          ? `$${Number(row.discountValue || 0).toFixed(2)}`
          : `${Number(
            row.discountValue !== undefined && row.discountValue !== null
              ? row.discountValue
              : row.discountPercentage || 0
          ).toFixed(2)}%`,
    },
    {
      field: 'discountType',
      headerName: 'Type',
      flex: 0.7,
    },
    {
      field: 'minOrderAmount',
      headerName: 'Min Order',
      flex: 0.75,
      renderCell: ({ row }) =>
        row.minOrderAmount ? `$${Number(row.minOrderAmount).toFixed(2)}` : 'None',
    },
    {
      field: 'usage',
      headerName: 'Usage',
      flex: 0.75,
      renderCell: ({ row }) => `${row.usedCount || 0} / ${row.maxUses ?? 'Unlimited'}`,
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      flex: 1,
      renderCell: ({ row }) =>
        row.expiresAt ? new Date(row.expiresAt).toLocaleString() : 'No expiry',
    },
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 0.65,
      renderCell: ({ row }) => (
        <Chip
          label={row.isActive ? 'Active' : 'Inactive'}
          color={row.isActive ? 'success' : 'default'}
          size='small'
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.7,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box>
          <Button
            size='small'
            onClick={() => handleEdit(row)}
            sx={{ minWidth: 'auto', color: '#6e0506' }}
          >
            <EditOutlined />
          </Button>
          <Button
            size='small'
            onClick={() => handleDelete(row.id)}
            sx={{ minWidth: 'auto', color: '#6e0506' }}
          >
            <Delete />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box color={'#6e0506'} m='20px'>
      <Header
        title='COUPONS'
        subtitle='Create and manage fixed or percentage-based discount codes'
      />
      <Box display='flex' justifyContent='flex-end' mb='20px'>
        <Button
          variant='contained'
          onClick={() => setOpenDialog(true)}
          startIcon={<LocalOfferOutlinedIcon />}
          sx={{
            backgroundColor: '#6e0506',
            '&:hover': {
              backgroundColor: '#8c1617',
            },
          }}
        >
          Add Coupon
        </Button>
      </Box>
      <Box
        m='20px 0 0 0'
        height='70vh'
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
            fontSize: '14px',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
            color: '#6e0506',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgba(112, 14, 16, 0.2)',
            borderTopLeftRadius: '5px',
            borderTopRightRadius: '5px',
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: 'none',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: 'rgba(112, 14, 16, 0.2)',
            borderBottomLeftRadius: '5px',
            borderBottomRightRadius: '5px',
          },
          '& .MuiCheckbox-root': {
            color: `#6e0506 !important`,
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(255, 206, 207, 0.5)',
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(112, 14, 16, 0.3)',
            borderRadius: '4px',
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(112, 14, 16, 0.5)',
          },
        }}
      >
        <DataGrid
          rows={coupons}
          columns={columns}
          loading={loading}
          components={{ Toolbar: GridToolbar }}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          sx={{ color: '#6e0506' }}
        />
      </Box>

      <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth='sm'>
        <DialogTitle>{editingCouponId ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap='16px' mt='8px'>
            <TextField
              label='Coupon Code'
              value={formData.code}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
              }
              fullWidth
            />
            <TextField
              label='Description'
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              fullWidth
            />
            <TextField
              select
              label='Discount Type'
              value={formData.discountType}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, discountType: e.target.value }))
              }
              fullWidth
            >
              <MenuItem value='percentage'>Percentage</MenuItem>
              <MenuItem value='fixed'>Fixed Amount</MenuItem>
            </TextField>
            <TextField
              label={
                formData.discountType === 'fixed'
                  ? 'Discount Amount'
                  : 'Discount Percentage'
              }
              type='number'
              value={formData.discountValue}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, discountValue: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label='Minimum Order Amount'
              type='number'
              value={formData.minOrderAmount}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, minOrderAmount: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label='Maximum Uses'
              type='number'
              value={formData.maxUses}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, maxUses: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label='Expiration Date'
              type='datetime-local'
              value={formData.expiresAt}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, expiresAt: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  sx={{
                    olor: '#6e0506',
                    '&:hover': {
                      color: '#8c1617',
                    },
                  }}
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                />
              }
              label='Coupon is active'
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ paddingRight: '20px' }}>
          <Button variant='outlined' onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant='contained' sx={{
            backgroundColor: '#6e0506',
            '&:hover': {
              backgroundColor: '#8c1617',
            },
          }}>
            {editingCouponId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Alert
        openSnackbar={openSnackbar}
        alertSeverity={snackbarSeverity}
        setOpenSnackbar={setOpenSnackbar}
        alertMessage={snackbarMessage}
      />
    </Box>
  );
};

export default Coupons;
